import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, api } from "../api/client.js";

/**
 * The only place a deep read starts.
 *
 * Discovery is tuned over-inclusive: a false positive costs a cheap local classification,
 * a false negative is a separation that stays invisible. That trade holds only while the
 * deep read stays behind a decision, so surfacing a situation is automatic and reading
 * one deeply is a press, made here, with the document's size visible first.
 *
 * `ruleDerivedTerms` is the count of terms matched by rule. A ratio and a record date
 * matched by rule carry a situation to `terms_set`, so the grid can be full while nothing
 * has read the filing, and this panel says so.
 *
 * @param situationId The situation whose filings the read would cover.
 * @param ruleDerivedTerms How many displayed terms came from the free rule pass.
 */
export function ExtractPanel({
  situationId,
  ruleDerivedTerms = 0,
}: {
  situationId: string;
  ruleDerivedTerms?: number;
}) {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  /**
   * Null means the server's default, which is not the same as naming the default filing:
   * holding null keeps the panel on the current best read as the situation gains
   * amendments, where pinning the accession would freeze whatever was newest then.
   */
  const [chosen, setChosen] = useState<string | null>(null);

  const state = useQuery({
    queryKey: ["extraction", situationId, chosen],
    queryFn: () => api.extractionState(situationId, chosen ?? undefined),
    // Only while a job is outstanding: a page open on a finished situation must not poll for
    // an answer that cannot change.
    refetchInterval: (q) => (q.state.data?.queued ? 4_000 : false),
  });

  /**
   * Nothing else on the page knows the read finished. Watching the transition refreshes the
   * terms, flags and situation once, when there is something new to show.
   */
  const wasQueued = useRef(false);
  useEffect(() => {
    const queued = state.data?.queued != null;
    if (wasQueued.current && !queued) {
      void queryClient.invalidateQueries({ queryKey: ["facts", situationId] });
      void queryClient.invalidateQueries({ queryKey: ["flags", situationId] });
      void queryClient.invalidateQueries({ queryKey: ["situation", situationId] });
      void queryClient.invalidateQueries({ queryKey: ["situations"] });
    }
    wasQueued.current = queued;
  }, [state.data?.queued, queryClient, situationId]);

  const run = useMutation({
    mutationFn: () => api.extract(situationId, chosen ?? undefined),
    onSuccess: async () => {
      setError(null);
      await queryClient.invalidateQueries({ queryKey: ["extraction", situationId] });
    },
    onError: (e) => {
      setError(e instanceof ApiError ? e.message : String(e));
    },
  });

  /**
   * Text this situation holds that an older extractor produced. Rendered only when
   * something is stale, which is not the ordinary case.
   */
  const stale = useQuery({
    queryKey: ["reextract", situationId],
    queryFn: () => api.reextract(situationId),
    refetchInterval: (q) =>
      q.state.data?.estimate?.filings?.some((f) => f.inFlight) ? 4_000 : false,
  });

  const reextract = useMutation({
    mutationFn: () => api.runReextract(situationId),
    onSuccess: async () => {
      setError(null);
      await queryClient.invalidateQueries({ queryKey: ["reextract", situationId] });
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : String(e)),
  });

  if (state.isLoading || !state.data) return null;
  const { target, queued, runs, candidates } = state.data;
  /*
   * Optional the whole way down because this notice is auxiliary: a newer bundle in front
   * of an older API answers this route with something that is not an estimate, and the
   * Deep read control must not go down with it.
   */
  const staleFilings = stale.data?.estimate?.filings ?? [];

  return (
    <>
      <div className="sechd">Deep read</div>
      <div className="deepread">
        {/*
          Which filing gets read, when more than one could.

          The default is the newest registration statement, which an amendment restates
          whole. An original Form 10 read whole can say more than a late amendment's
          delta, and an 8-K states the parent and a timeframe differently.

          `candidates[0]` is the server's default, from the same ordering, so leaving
          this alone selects what the button would read anyway.
        */}
        {candidates.length > 1 && (
          <label className="dr-pick">
            <span>Read</span>
            <select
              value={chosen ?? ""}
              onChange={(e) => setChosen(e.target.value || null)}
              disabled={queued !== null || run.isPending}
            >
              <option value="">
                {`Best available — ${candidates[0]!.formType} ${candidates[0]!.filingDate}`}
              </option>
              {candidates.map((c) => (
                <option key={c.accession} value={c.accession}>
                  {`${c.formType} · ${c.filingDate} · ${c.charCount.toLocaleString()} chars`}
                  {c.lastReadAt ? " · read" : ""}
                </option>
              ))}
            </select>
          </label>
        )}

        {target ? (
          <div className="dr-doc">
            <b>{target.formType}</b> · {target.filingDate} · {target.filename} ·{" "}
            {target.charCount.toLocaleString()} characters ·{" "}
            {"~"}
            {Math.round(target.estimate.tokens / 1000).toLocaleString()}k tokens
            {/*
              An amendment is read as a diff against the version before it, so the
              character count above is the whole document, not what gets sent.
            */}
            {target.priorAccession ? (
              <div className="dr-note">
                Read as a delta against the previous registration statement. Choose the
                original above to read the whole document.
              </div>
            ) : null}
          </div>
        ) : (
          <div className="dr-doc">
            No filing in this situation has extracted text yet — it is probably still in
            the fetch queue.
          </div>
        )}

        {ruleDerivedTerms > 0 && runs.length === 0 && (
          <div className="dr-note dr-rule">
            {ruleDerivedTerms} term{ruleDerivedTerms === 1 ? " above was" : "s above were"} matched
            by rule from the front of a filing. Nothing has read this situation.
          </div>
        )}

        {runs.length > 0 && (
          <ul className="dr-runs">
            {runs.map((r) => (
              <li key={r.at}>
                read {r.at.slice(0, 10)} · {r.model}
                {/*
                  A subscription read consumed real tokens and cost no dollars, so "$0.00"
                  would misstate it. A `worker` row was bought on the API key, and what it
                  cost is part of the record.
                */}
                {r.executor === "agent"
                  ? " · on subscription"
                  : ` · $${Number(r.costUsd).toFixed(2)} on the API key`}
              </li>
            ))}
          </ul>
        )}

        {/*
          Nothing in the API process runs this: a press queues work for a Claude Code
          session that may not run for a while, and unexplained, `pending` reads as a
          stall.
        */}
        {queued ? (
          <div className="dr-state">
            {queued.state === "running"
              ? "A session is reading it now…"
              : "Queued for a Claude Code session."}{" "}
            The terms above will fill in when it lands.
          </div>
        ) : target ? (
          <div className="dr-act">
            <button
              className="iconbtn dr-go"
              disabled={run.isPending}
              onClick={() => run.mutate()}
            >
              {run.isPending
                ? "Queueing…"
                : runs.length > 0
                  ? "Read again"
                  : "Read with Claude"}{" "}
              · {Math.round(target.estimate.tokens / 1000).toLocaleString()}k tokens
            </button>
            {runs.length > 0 && (
              // The guard against reading twice lives in `extraction_runs`, keyed on the
              // prompt, so an unchanged document is answered from storage.
              <span className="dr-note">
                An unchanged document re-reads from the stored answer.
              </span>
            )}
          </div>
        ) : null}

        {/*
          A queued read nothing can claim: without a configured agent token there is no
          executor, and the situation never fills in.
        */}
        {state.data && !state.data.agentConfigured && (
          <div className="dr-note dr-rule">
            No agent token is configured, so nothing can claim a queued read. Set
            MOSAIC_AGENT_TOKEN on the deployment first.
          </div>
        )}

        {/*
          Re-extraction, offered only when there is text an older extractor produced.

          Separate from the read above, and says so: the queue has no dependency edge, so
          a read queued alongside would build its prompt from the text still stored, hash
          identically to the last run, and return the cached answer.
        */}
        {staleFilings.length > 0 && (
          <div className="dr-note dr-rule">
            {staleFilings.length} filing{staleFilings.length === 1 ? "" : "s"} here{" "}
            {staleFilings.length === 1 ? "was" : "were"} extracted by an older version of
            the reader (now {stale.data?.estimate.extractorVersion}). Re-extracting
            rewrites the stored text and re-indexes it; the read above is a separate press
            afterwards, and it will pay, because the document will have changed.
            <div className="dr-act">
              <button
                className="iconbtn"
                disabled={reextract.isPending || staleFilings.every((f) => f.inFlight)}
                onClick={() => reextract.mutate()}
              >
                {reextract.isPending
                  ? "Queueing…"
                  : staleFilings.every((f) => f.inFlight)
                    ? "Re-extracting…"
                    : `Re-extract ${staleFilings.length} filing${staleFilings.length === 1 ? "" : "s"}`}
              </button>
            </div>
          </div>
        )}

        {error && <div className="dr-err">{error}</div>}
      </div>
    </>
  );
}
